import { PartCreationWizardState, SitePartOperation } from "./types.ts";
import {
	PartCreationWizardSubmitData_BomLine,
	PartCreationWizardSubmitData_CatalogPart,
	PartCreationWizardSubmitData_CatalogPartBom,
	PartCreationWizardSubmitData_PartWarehouseLocation,
	PartCreationWizardSubmitData_SalesPart,
	PartCreationWizardSubmitData_SitePart,
	PartCreationWizardSubmitData_SitePartOperation,
	PartCreationWizardSubmitData_SupplierPart,
} from "src/api/generated/erp/parts/catalogPart/service/partCreationWizardSubmitData.ts";
import { CatalogPartNumberDefiner } from "src/api/generated/erp/parts/catalogPart/service/catalogPartNumberDefiner.ts";
import {
	CatalogPartRevisionBomDataGridRow,
} from "src/components/views/erp/parts/catalogPart/CatalogPartRevisionBomDataGrid.utils.tsx";

export interface PartCreationWizardSubmitData {
	catalogPart: PartCreationWizardSubmitData_CatalogPart;
	catalogPartBom: PartCreationWizardSubmitData_CatalogPartBom | null;
	sitePart: PartCreationWizardSubmitData_SitePart | null;
	sitePartOperations: PartCreationWizardSubmitData_SitePartOperation[] | null;
	salesPart: PartCreationWizardSubmitData_SalesPart | null;
	supplierPart: PartCreationWizardSubmitData_SupplierPart | null;
	partWarehouseLocation: PartCreationWizardSubmitData_PartWarehouseLocation | null;
}

export function convertWizardStateToSubmitData(wizardState: PartCreationWizardState): PartCreationWizardSubmitData {
	const createManufacturedSitePart =
		wizardState.createSitePart &&
		wizardState.sitePart.siteId != null &&
		wizardState.sitePart.partType === "MANUFACTURE";

	return {
		catalogPart: convertCatalogPart(wizardState),
		catalogPartBom:
			wizardState.catalogPartBom.length > 0 ?
				{
					bomLines: wizardState.catalogPartBom.map(convertBomLine),
				}
			:	null,
		sitePart: convertSitePart(wizardState),
		sitePartOperations:
			createManufacturedSitePart && wizardState.sitePartOperations.length > 0 ?
				wizardState.sitePartOperations.map(convertSitePartOperation)
			:	null,
		salesPart: wizardState.createSalesPart ? convertSalesPart(wizardState) : null,
		supplierPart: shouldConvertSupplierPart(wizardState) ? convertSupplierPart(wizardState) : null,
		partWarehouseLocation:
			wizardState.createPartWarehouseLocation ? convertPartWarehouseLocation(wizardState) : null,
	};
}

export function shouldConvertSupplierPart(wizardState: PartCreationWizardState): boolean {
	return wizardState.createSupplierPart && wizardState.sitePart.partType === "PURCHASE";
}

export function isValidCatalogPartBom(bomLines: Partial<CatalogPartRevisionBomDataGridRow>[]): boolean {
	return bomLines.every((line) => (
		isPositiveNumber(line.lineCatalogPartId) &&
		isPositiveNumber(line.referenceNo) &&
		isNonNegativeNumber(line.quantity) &&
		typeof line.acquisitionMethod === "string" &&
		typeof line.isPhantom === "boolean"
	));
}

export function isValidSitePartOperations(operations: Partial<SitePartOperation>[]): boolean {
	return operations.every((operation) => (
		isPositiveNumber(operation.operationId) &&
		typeof operation.materialOperationRowId === "string" &&
		operation.materialOperationRowId.length > 0 &&
		isOptionalNonNegativeNumber(operation.setUpTime) &&
		isOptionalNonNegativeNumber(operation.workingTime)
	));
}

function convertCatalogPart({ catalogPart }: PartCreationWizardState): PartCreationWizardSubmitData_CatalogPart {
	return {
		additionalNumber: catalogPart.additionalNumber ?? "",
		categoryId: expectValue(catalogPart.categoryId, "catalog part category"),
		configurationProductFamilyId:
			catalogPart.isConfigurable ? expectValue(catalogPart.configurationProductFamilyId, "product family") : null,
		description_1: expectValue(catalogPart.description_1, "catalog part description"),
		description_2: catalogPart.description_2 ?? "",
		designUnitFactor: expectValue(catalogPart.designUnitFactor, "design unit factor"),
		designUnitId: expectValue(catalogPart.designUnitId, "design unit"),
		isConfigurable: catalogPart.isConfigurable ?? false,
		manufacturerPartNumber: catalogPart.manufacturerPartNumber ?? "",
		partNoDefiner: convertPartNumberDefiner(catalogPart),
		partUnitId: expectValue(catalogPart.partUnitId, "part unit"),
		responsiblePersonId: expectValue(catalogPart.responsiblePersonId, "responsible person"),
		weight: catalogPart.weight,
	};
}

function convertBomLine(row: CatalogPartRevisionBomDataGridRow): PartCreationWizardSubmitData_BomLine {
	return {
		acquisitionMethod: expectValue(row.acquisitionMethod, "BOM line acquisition method"),
		bomLineGroupId: row.bomLineGroupId,
		configuratorTransformationScript: row.configuratorTransformationScript ?? "",
		isPhantom: row.isPhantom ?? false,
		lineCatalogPartId: expectValue(row.lineCatalogPartId, "BOM line part"),
		note: row.note ?? "",
		quantity: expectValue(row.quantity, "BOM line quantity"),
		referenceNo: expectValue(row.referenceNo, "BOM line reference number"),
	};
}

function convertSitePart({
	createSitePart,
	sitePart,
}: PartCreationWizardState): PartCreationWizardSubmitData_SitePart | null {
	if (!createSitePart || sitePart.siteId == null) return null;

	const isManufacture = sitePart.partType === "MANUFACTURE";

	return {
		acquisitionMethod: expectValue(sitePart.acquisitionMethod, "site part acquisition method"),
		acquisitionRemark: sitePart.acquisitionRemark ?? "",
		aggregateOnPurchaseOrder: sitePart.aggregateOnPurchaseOrder ?? false,
		capacityQuantity: isManufacture ? sitePart.capacityQuantity : null,
		defaultProductionLineId: isManufacture ? sitePart.defaultProductionLineId : null,
		partType: expectValue(sitePart.partType, "site part type"),
		responsiblePersonId: expectValue(sitePart.responsiblePersonId, "site part responsible person"),
		salesRemark: sitePart.salesRemark ?? "",
		siteId: sitePart.siteId,
		standardCost: expectValue(sitePart.standardCost, "site part cost"),
		valuationMethod: expectValue(sitePart.valuationMethod, "site part valuation method"),
		warehouseTransferFactor: expectValue(sitePart.warehouseTransferFactor, "warehouse transfer factor"),
		warehouseTransferUnitId: expectValue(sitePart.warehouseTransferUnitId, "warehouse transfer unit"),
	};
}

function convertSitePartOperation(operation: SitePartOperation): PartCreationWizardSubmitData_SitePartOperation {
	return {
		configuratorTransformationScript: operation.configuratorTransformationScript ?? "",
		materialOperationRowId: expectValue(operation.materialOperationRowId, "material operation"),
		operationId: expectValue(operation.operationId, "operation"),
		rowId: operation.rowId,
		setUpTime: operation.setUpTime,
		workingTime: operation.workingTime,
	};
}

function convertSalesPart({ salesPart }: PartCreationWizardState): PartCreationWizardSubmitData_SalesPart {
	return {
		accountingCodeIds: salesPart.accountingDimensionValues?.map((value) => value?.accountingCodeId) ?? [],
		automaticDelivery: salesPart.automaticDelivery ?? false,
		canBeUsedOnCustomerOfferLine: salesPart.canBeUsedOnCustomerOfferLine ?? false,
		canBeUsedOnCustomerOrderLine: salesPart.canBeUsedOnCustomerOrderLine ?? false,
		configurationDefaultPricingMethod: expectValue(
			salesPart.configurationDefaultPricingMethod,
			"configuration default pricing method",
		),
		hasTemplateConfiguration: salesPart.hasTemplateConfiguration ?? false,
		importCodes: salesPart.importCodes?.flatMap((code) => code?.importCode ?? []) ?? [],
		priceFactor: expectValue(salesPart.priceFactor, "sales price factor"),
		salesFactor: expectValue(salesPart.salesFactor, "sales factor"),
		salesPartCategoryId: salesPart.salesPartCategoryId,
		salesPartDescription: expectValue(salesPart.salesPartDescription, "sales part description"),
		salesPartNo: salesPart.salesPartNo ?? "",
		salesPartPriceGroupId: salesPart.salesPartPriceGroupId,
		salesPartType: expectValue(salesPart.salesPartType, "sales part type"),
		salesPrice: expectValue(salesPart.salesPrice, "sales price"),
		salesPriceUnitId: expectValue(salesPart.salesPriceUnitId, "sales price unit"),
		salesUnitId: expectValue(salesPart.salesUnitId, "sales unit"),
		vatCodeId: expectValue(salesPart.vatCodeId, "VAT code"),
	};
}

function convertSupplierPart({ supplierPart }: PartCreationWizardState): PartCreationWizardSubmitData_SupplierPart {
	return {
		deliveryDuration: expectValue(supplierPart.deliveryDuration, "delivery duration"),
		packSize: expectValue(supplierPart.packSize, "pack size"),
		purchaseFactor: expectValue(supplierPart.purchaseFactor, "purchase factor"),
		purchasePrice: expectValue(supplierPart.purchasePrice, "purchase price"),
		purchasePriceFactor: expectValue(supplierPart.purchasePriceFactor, "purchase price factor"),
		purchasePriceUnitId: expectValue(supplierPart.purchasePriceUnitId, "purchase price unit"),
		purchaseUnitId: expectValue(supplierPart.purchaseUnitId, "purchase unit"),
		supplierId: expectValue(supplierPart.supplierId, "supplier"),
		supplierPartDescription: supplierPart.supplierPartDescription ?? "",
		supplierPartNo: supplierPart.supplierPartNo ?? "",
	};
}

function convertPartWarehouseLocation(
	{ partWarehouseLocation }: PartCreationWizardState,
): PartCreationWizardSubmitData_PartWarehouseLocation {
	const usesWarehouseTransfer = partWarehouseLocation.replenishmentMethod === "WAREHOUSE_TRANSFER";

	return {
		askQuantity: partWarehouseLocation.askQuantity ?? false,
		askTransferInfo: partWarehouseLocation.askTransferInfo ?? false,
		defaultReplenishmentQuantity: partWarehouseLocation.defaultReplenishmentQuantity ?? 1,
		givingPartWarehouseLocationId:
			usesWarehouseTransfer ?
				expectValue(partWarehouseLocation.givingPartWarehouseLocationId, "giving warehouse location")
			:	null,
		givingSiteId: usesWarehouseTransfer ? expectValue(partWarehouseLocation.givingSiteId, "giving site") : null,
		inventoryLevelEnabled: partWarehouseLocation.inventoryLevelEnabled ?? false,
		locationQuantity: expectValue(partWarehouseLocation.locationQuantity, "warehouse location quantity"),
		replenishmentMethod: partWarehouseLocation.replenishmentMethod,
		replenishmentTrigger: partWarehouseLocation.replenishmentTrigger ?? "MANUAL",
		warehouseLocationId: expectValue(partWarehouseLocation.warehouseLocationId, "warehouse location"),
	};
}

function convertPartNumberDefiner(catalogPart: PartCreationWizardState["catalogPart"]): CatalogPartNumberDefiner {
	if (catalogPart.partNumberInsertType === "manuallyInsertedPartNumber") {
		return {
			type: "partNumber",
			partNo: expectNonBlankString(catalogPart.manuallyInsertedPartNumber, "part number").trim(),
		};
	}

	return {
		type: "numberSequenceId",
		numberSequenceId: expectValue(catalogPart.numberSequenceId, "number sequence"),
	};
}

function expectValue<T>(value: T | null | undefined, fieldLabel: string): T {
	if (value == null) {
		throw new Error(
			`Part creation wizard submitted without ${fieldLabel}. This should have been caught by form validation.`,
		);
	}
	return value;
}

function expectNonBlankString(value: string | null | undefined, fieldLabel: string): string {
	const nonNullValue = expectValue(value, fieldLabel);
	if (nonNullValue.trim().length === 0) {
		throw new Error(
			`Part creation wizard submitted without ${fieldLabel}. This should have been caught by form validation.`,
		);
	}
	return nonNullValue;
}

function isPositiveNumber(value: unknown): value is number {
	return typeof value === "number" && value > 0;
}

function isNonNegativeNumber(value: unknown): value is number {
	return typeof value === "number" && value >= 0;
}

function isOptionalNonNegativeNumber(value: unknown): boolean {
	return value == null || isNonNegativeNumber(value);
}
